import React from 'react';
import { Card } from '../components/ui/Card';
import { formatCurrency } from '../utils/calculations';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
export function JobProfit() {
  const jobData = [{
    id: 'job-1',
    name: 'GarrettAuto.com Website',
    customer: 'Garrett Auto',
    revenue: 1500,
    costs: {
      labor: 800,
      materials: 200,
      overhead: 150
    },
    status: 'In Progress'
  }, {
    id: 'job-2',
    name: 'SamsDisposal.com Redesign',
    customer: 'Sams Disposal',
    revenue: 5000,
    costs: {
      labor: 2200,
      materials: 500,
      overhead: 400
    },
    status: 'In Progress'
  }, {
    id: 'job-3',
    name: 'DesertSageSeminars.com',
    customer: 'Desert Sage Seminars',
    revenue: 8200,
    costs: {
      labor: 3500,
      materials: 800,
      overhead: 600
    },
    status: 'In Progress'
  }, {
    id: 'job-4',
    name: 'Echo AWS Infrastructure',
    customer: 'Echo',
    revenue: 50000,
    costs: {
      labor: 18000,
      materials: 12000,
      overhead: 3000
    },
    status: 'In Progress'
  }, {
    id: 'job-5',
    name: 'LuckyDuckBath.com',
    customer: 'Lucky Duck Bath',
    revenue: 2500,
    costs: {
      labor: 1100,
      materials: 300,
      overhead: 200
    },
    status: 'In Progress'
  }, {
    id: 'job-6',
    name: 'TeamNTA.com',
    customer: 'Team NTA',
    revenue: 2750,
    costs: {
      labor: 1200,
      materials: 350,
      overhead: 220
    },
    status: 'In Progress'
  }];
  const jobsWithProfit = jobData.map(job => {
    const totalCosts = job.costs.labor + job.costs.materials + job.costs.overhead;
    const profit = job.revenue - totalCosts;
    const margin = profit / job.revenue * 100;
    return {
      ...job,
      totalCosts,
      profit,
      margin
    };
  });
  const totalRevenue = jobsWithProfit.reduce((sum, job) => sum + job.revenue, 0);
  const totalCosts = jobsWithProfit.reduce((sum, job) => sum + job.totalCosts, 0);
  const totalProfit = totalRevenue - totalCosts;
  const averageMargin = totalProfit / totalRevenue * 100;
  const mostProfitableJob = jobsWithProfit.reduce((max, job) => job.profit > max.profit ? job : max);
  const highestMarginJob = jobsWithProfit.reduce((max, job) => job.margin > max.margin ? job : max);
  return <div className="space-y-6" data-id="element-1683">
      <div data-id="element-1684">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1685">Job Profit</h1>
        <p className="text-sm text-gray-600" data-id="element-1686">
          Track profitability by job or project
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1687">
        <Card data-id="element-1688">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1689">Total Revenue</p>
          <p className="text-2xl font-bold text-blue-600" data-id="element-1690">
            {formatCurrency(totalRevenue)}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1691">
            {jobsWithProfit.length} active jobs
          </p>
        </Card>

        <Card data-id="element-1692">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1693">Total Costs</p>
          <p className="text-2xl font-bold text-red-600" data-id="element-1694">
            {formatCurrency(totalCosts)}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1695">All jobs combined</p>
        </Card>

        <Card data-id="element-1696">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1697">Total Profit</p>
          <p className="text-2xl font-bold text-green-600" data-id="element-1698">
            {formatCurrency(totalProfit)}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1699">
            {averageMargin.toFixed(1)}% avg margin
          </p>
        </Card>

        <Card data-id="element-1700">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1701">Best Margin</p>
          <p className="text-2xl font-bold text-purple-600" data-id="element-1702">
            {highestMarginJob.margin.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1703">{highestMarginJob.name}</p>
        </Card>
      </div>

      {/* Jobs Table */}
      <Card data-id="element-1704">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1705">
          Job Profitability
        </h2>
        <div className="overflow-x-auto" data-id="element-1706">
          <table className="w-full" data-id="element-1707">
            <thead data-id="element-1708">
              <tr className="border-b-2 border-gray-200" data-id="element-1709">
                <th className="text-left py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1710">
                  Job Name
                </th>
                <th className="text-left py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1711">
                  Customer
                </th>
                <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1712">
                  Revenue
                </th>
                <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1713">
                  Costs
                </th>
                <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1714">
                  Profit
                </th>
                <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1715">
                  Margin
                </th>
                <th className="text-center py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1716">
                  Status
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1717">
              {jobsWithProfit.map(job => <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1718">
                  <td className="py-3 px-2 text-sm font-medium text-gray-900" data-id="element-1719">
                    {job.name}
                  </td>
                  <td className="py-3 px-2 text-sm text-gray-600" data-id="element-1720">
                    {job.customer}
                  </td>
                  <td className="py-3 px-2 text-sm text-right font-medium text-gray-900" data-id="element-1721">
                    {formatCurrency(job.revenue)}
                  </td>
                  <td className="py-3 px-2 text-sm text-right text-red-600" data-id="element-1722">
                    {formatCurrency(job.totalCosts)}
                  </td>
                  <td className="py-3 px-2 text-sm text-right font-semibold text-green-600" data-id="element-1723">
                    {formatCurrency(job.profit)}
                  </td>
                  <td className="py-3 px-2 text-sm text-right" data-id="element-1724">
                    <span className={`inline-flex items-center gap-1 ${job.margin >= 50 ? 'text-green-600' : job.margin >= 30 ? 'text-blue-600' : 'text-orange-600'}`} data-id="element-1725">
                      {job.margin >= 50 ? <TrendingUpIcon className="w-3 h-3" data-id="element-1726" /> : job.margin < 30 ? <TrendingDownIcon className="w-3 h-3" data-id="element-1727" /> : null}
                      <span className="font-semibold" data-id="element-1728">
                        {job.margin.toFixed(1)}%
                      </span>
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center" data-id="element-1729">
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded" data-id="element-1730">
                      {job.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
            <tfoot data-id="element-1731">
              <tr className="border-t-2 border-gray-300 bg-gray-50" data-id="element-1732">
                <td className="py-3 px-2 text-sm font-bold text-gray-900" colSpan={2} data-id="element-1733">
                  Totals
                </td>
                <td className="py-3 px-2 text-sm text-right font-bold text-blue-600" data-id="element-1734">
                  {formatCurrency(totalRevenue)}
                </td>
                <td className="py-3 px-2 text-sm text-right font-bold text-red-600" data-id="element-1735">
                  {formatCurrency(totalCosts)}
                </td>
                <td className="py-3 px-2 text-sm text-right font-bold text-green-600" data-id="element-1736">
                  {formatCurrency(totalProfit)}
                </td>
                <td className="py-3 px-2 text-sm text-right font-bold text-gray-900" data-id="element-1737">
                  {averageMargin.toFixed(1)}%
                </td>
                <td data-id="element-1738"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* Cost Breakdown for Top Job */}
      <Card data-id="element-1739">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1740">
          Cost Breakdown: {mostProfitableJob.name}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1741">
          <div className="p-4 bg-blue-50 rounded-lg" data-id="element-1742">
            <p className="text-sm text-gray-600 mb-1" data-id="element-1743">Labor Costs</p>
            <p className="text-xl font-bold text-blue-600" data-id="element-1744">
              {formatCurrency(mostProfitableJob.costs.labor)}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-1745">
              {(mostProfitableJob.costs.labor / mostProfitableJob.totalCosts * 100).toFixed(0)}
              % of total costs
            </p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg" data-id="element-1746">
            <p className="text-sm text-gray-600 mb-1" data-id="element-1747">Materials</p>
            <p className="text-xl font-bold text-orange-600" data-id="element-1748">
              {formatCurrency(mostProfitableJob.costs.materials)}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-1749">
              {(mostProfitableJob.costs.materials / mostProfitableJob.totalCosts * 100).toFixed(0)}
              % of total costs
            </p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg" data-id="element-1750">
            <p className="text-sm text-gray-600 mb-1" data-id="element-1751">Overhead</p>
            <p className="text-xl font-bold text-purple-600" data-id="element-1752">
              {formatCurrency(mostProfitableJob.costs.overhead)}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-1753">
              {(mostProfitableJob.costs.overhead / mostProfitableJob.totalCosts * 100).toFixed(0)}
              % of total costs
            </p>
          </div>
        </div>
      </Card>
    </div>;
}